import React, { useEffect, useRef, useState } from 'react';
import { signIn, isSignedIn, initAuth } from '../services/auth';
import { acceptInvite } from '../services/api';
import { PublicHeader, PublicFooter } from './LandingPage';
import { Loading } from '../components/ui';

// Landing page for an invite link (/accept-invite?token=...). Ported from the
// old app's AcceptInvite.jsx - same four outcomes, same wording - but the
// sign-in round trip is rebuilt for MSAL. The old app was Static Web Apps
// hosted and could bounce through /.auth/login/aad with a post_login_redirect_uri
// that brought the user back to this exact URL; MSAL here is configured with
// redirectUri = window.location.origin and navigateToLoginRequestUrl: false,
// so a signed-out visitor always returns to "/" and the token in the query
// string is lost.
//
// Rather than register another Entra redirect URI - configuration the operator
// would have to do before a single invite worked - the token is parked in
// sessionStorage (which survives the redirect, and is where MSAL keeps its own
// state) and picked up again on the way back in. See resumePendingInvite in
// App.jsx for the other half.
const PENDING_KEY = 'ydyai_pending_invite';

export function stashPendingInvite(token) {
  try { sessionStorage.setItem(PENDING_KEY, token); } catch { /* private mode */ }
}

// Non-destructive check, for deciding whether to render this page at all.
export function peekPendingInvite() {
  try { return sessionStorage.getItem(PENDING_KEY) || ''; } catch { return ''; }
}

export function takePendingInvite() {
  try {
    const token = sessionStorage.getItem(PENDING_KEY) || '';
    if (token) sessionStorage.removeItem(PENDING_KEY);
    return token;
  } catch { return ''; }
}

export default function AcceptInvitePage() {
  const [status, setStatus] = useState('working'); // working | done | mismatch | error
  const [message, setMessage] = useState('');
  const [workspace, setWorkspace] = useState('');
  // React 18 StrictMode double-invokes effects in development; accepting twice
  // would clear the token on the first pass and 404 on the second.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || takePendingInvite();
    if (!token) {
      setStatus('error');
      setMessage('This invitation link is missing its token.');
      return;
    }

    (async () => {
      // This page renders ahead of App's auth gate (an invitee has no account
      // yet, so it must be reachable signed-out), which means MSAL has not
      // been initialised for us. Asking isSignedIn() before initialize() and
      // handleRedirectPromise() have run either throws or reports a signed-in
      // user as signed out, sending them through a pointless second login.
      try {
        await initAuth();
      } catch {
        setStatus('error');
        setMessage("Sign-in isn't configured for this site, so the invitation can't be completed.");
        return;
      }

      if (!isSignedIn()) {
        // Sign in, then resume: App.jsx routes back here once MSAL returns.
        stashPendingInvite(token);
        await signIn();
        return;
      }

      let data;
      try {
        data = await acceptInvite(token);
      } catch {
        setStatus('error');
        setMessage('Network error — please try again.');
        return;
      }

      if (data?.ok) {
        setWorkspace(data.workspace || '');
        setStatus('done');
        setTimeout(() => { window.location.href = '/projects'; }, 1400);
      } else if (data?.error === 'email_mismatch') {
        setStatus('mismatch');
        setMessage(`This invitation was sent to ${data.invitedEmail}. Please sign in with that account.`);
      } else if (data?.status === 410 || data?.error === 'invite_expired') {
        setStatus('error');
        setMessage('This invitation has expired. Ask whoever invited you to send a new one.');
      } else if (data?.status === 404 || data?.error === 'invalid_or_expired') {
        setStatus('error');
        setMessage('This invitation is no longer valid. Ask whoever invited you to send a new one.');
      } else {
        setStatus('error');
        setMessage(data?.error || "We couldn't accept this invitation.");
      }
    })();
  }, []);

  return (
    <div className="public">
      <PublicHeader showLogin={false} />
      <main className="public-main page-narrow">
        <div className="card" style={{ textAlign: 'center' }}>
          {status === 'working' && (
            <>
              <h1>Joining your workspace…</h1>
              <p className="sub">One moment while we set up your access.</p>
              <Loading text="Accepting invitation…" />
            </>
          )}
          {status === 'done' && (
            <>
              <div className="welcome-tick">✓</div>
              <h1>You're in!</h1>
              <p className="sub">
                Your seat{workspace ? ` in ${workspace}` : ''} is active. Taking you to your workspace…
              </p>
              <button type="button" className="btn" onClick={() => { window.location.href = '/projects'; }}>
                Go to workspace
              </button>
            </>
          )}
          {status === 'mismatch' && (
            <>
              <h1>Wrong account</h1>
              <p className="sub">{message}</p>
              <button type="button" className="btn" onClick={signIn}>Sign in with a different account</button>
            </>
          )}
          {status === 'error' && (
            <>
              <h1>Invitation problem</h1>
              <p className="sub">{message}</p>
            </>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
